import { Component, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ControlValueAccessorComponent } from '../../core';
import { Agb, AgbAgreement, Language } from '../../models';
import {
  AbstractI18nService,
  DialogService,
  UserService,
} from '../../services';
import { takeUntil } from 'rxjs/operators';
import { AgbDialogComponent, AgbDialogComponentData } from '../agb-dialog';
import { UserUtils } from '../../utils';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'lib-agb-form',
  templateUrl: './agb-form.component.html',
  styleUrls: ['./agb-form.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    // material design
    MatCheckboxModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AgbFormComponent),
      multi: true,
    },
  ],
})
export class AgbFormComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  public agbControl = new FormControl(false, [Validators.requiredTrue]);

  private agb: Agb;
  private selectedLanguage: Language;

  constructor(
    private dialogService: DialogService,
    private i18nService: AbstractI18nService,
    private userService: UserService
  ) {
    super();
    this.agbControl.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((v) => {
        if (v && this.agb?.version) {
          this._onChange({
            accepted: true,
            lastSeenVersion: this.agb.version,
          } as AgbAgreement);
        } else {
          this._onChange(null);
        }
      });

    this.i18nService.selectedLanguage$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((l) => (this.selectedLanguage = l));

    this.fetchAgb();
  }

  public displayAgbDialog(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.dialogService.openDialog(
      AgbDialogComponent,
      this.dialogService.createLargeDialogConfig({
        agb: this.agb,
      } as AgbDialogComponentData)
    );

    this.agbControl.setValue(true);
  }

  private async fetchAgb() {
    this.agb = await this.userService.getAgb(
      UserUtils.getAgbLocaleFromSelectedLanguage(this.selectedLanguage)
    );
  }

  // required for NG_VALUE_ACCESSOR
  writeValue(agbAgreement: AgbAgreement) {
    this.agbControl.setValue(!!agbAgreement?.accepted);
  }

  // required for NG_VALUE_ACCESSOR
  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.agbControl.disable();
    } else {
      this.agbControl.enable();
    }
  }
}
